/**
 * Every user-facing string on /about (the AI Spine page, ADR 0028), as data.
 * The page component only maps over this, so copy edits never touch layout, and
 * vocabulary.test.ts can hold the copy to the App Store vocabulary.
 */

export interface Capability {
  n: string;
  title: string;
  body: string;
  highlight: boolean;
}

export interface FitLayer {
  id: 'apps' | 'spine' | 'foundations';
  label: string;
  items: readonly string[];
}

export interface BuildStep {
  n: string;
  title: string;
  body: string;
}

export interface HoodTile {
  title: string;
  code?: string | undefined;
  body: string;
}

/** The browser tab title for /about. */
export const ABOUT_DOCUMENT_TITLE = 'The AI Spine — Growth Platforms App Store';

export const ABOUT_HERO = {
  eyebrow: 'The AI Spine',
  titleLead: 'One spine.',
  titleEmphasis: 'Every',
  titleTail: 'Growth Platforms app.',
  lede:
    "The AI Spine is the shared foundation under the Growth Platforms App Store. It gives every app sign-in, access control, trusted data and built-in AI, so teams build what's different and leave the plumbing to the platform.",
  primaryCta: 'Start building →',
  secondaryCta: 'Talk to GP Technology',
  orbitCore: 'AI Spine',
  orbitCoreKicker: 'The',
  orbitCardTitle: 'WorkSpan Warehouse',
  orbitCardCaption: 'Co-sell pipeline · Q2',
} as const;

export const ABOUT_SIGNALS: readonly string[] = [
  'Agentic & generative AI',
  'One sign-in',
  'Trusted data',
  'The right access',
  'Safe to ship',
];

export const CAPABILITIES_SECTION = {
  kicker: 'What the Spine gives every app',
  title: 'Nine things you never build again.',
  sub: 'An app on the App Store is just its own screens. Everything below comes built in from day one.',
} as const;

export const CAPABILITIES: readonly Capability[] = [
  {
    n: '01',
    title: 'Agentic and generative AI, built in',
    body: 'Every app can use agentic and generative AI, with no keys to manage and tracked metrics.',
    highlight: true,
  },
  {
    n: '02',
    title: 'An AI assistant in every app',
    body: "An AI assistant that knows which page you're on, works with your app's own tools and instructions, and answers in context.",
    highlight: true,
  },
  {
    n: '03',
    title: 'Trusted data',
    body: 'Live data feeds with sensitive fields kept out automatically.',
    highlight: false,
  },
  {
    n: '04',
    title: 'Email, ready to send',
    body: 'Send notifications, calls to action and reminders to colleagues from a trusted Deloitte address. No mail setup needed.',
    highlight: false,
  },
  {
    n: '05',
    title: 'One sign-in',
    body: 'Your Deloitte login works in every app. Nothing new to remember.',
    highlight: false,
  },
  {
    n: '06',
    title: 'The right access for everyone',
    body: 'People see the apps and alliances they should, and nothing else.',
    highlight: false,
  },
  {
    n: '07',
    title: 'One home, one look',
    body: 'Shared navigation, layout and Deloitte styling, in light or dark mode, so every app feels like one product.',
    highlight: false,
  },
  {
    n: '08',
    title: 'Safe to ship',
    body: 'Every app is reviewed, switched on deliberately, and deploys only to its own space.',
    highlight: false,
  },
  {
    n: '09',
    title: 'The builder kit',
    body: 'A starter app, the platform toolkit, and an AI skill that builds alongside you.',
    highlight: false,
  },
];

export const FIT_SECTION = {
  kicker: 'How it fits together',
  title: 'Apps on top. The Spine underneath.',
  sub: 'Each app is built by its own team, in its own repo, and plugs into the same spine.',
  connector: 'plugs in',
} as const;

/* Exempt from the vocabulary guard: the Foundations layer names Anthropic Claude
   by design (ADR 0028). The app names are copy, not registry data, but each one is a
   real App Store tile name: rename them here when the tiles change. */
export const FIT_LAYERS: readonly FitLayer[] = [
  {
    id: 'apps',
    label: 'Your apps',
    items: ['WorkSpan Warehouse', 'Formations', 'Leadership Agendas', 'Your app next →'],
  },
  {
    id: 'spine',
    label: 'The AI Spine',
    items: ['AI & assistant', 'Trusted data', 'Email', 'Sign-in', 'Access', 'Shell & navigation', 'Review & deploy'],
  },
  {
    id: 'foundations',
    label: 'Foundations',
    items: ['Deloitte SSO', 'Anthropic Claude', 'AirTable & data sources', 'Deloitte AWS'],
  },
];

export const BUILD_SECTION = {
  kicker: 'Build on the Spine',
  title: 'From idea to live app in four steps.',
  sub: 'No AWS access needed to start. Most teams build their first screen in an afternoon with the AI skill.',
  primaryCta: 'Start building →',
  guideCta: 'Read the builder guide',
  guideUrl: 'https://github.com/Deloitte-US-Consulting/gp-platform/blob/main/docs/building-an-app.md',
} as const;

export const BUILD_STEPS: readonly BuildStep[] = [
  { n: '01', title: 'Start from the template', body: 'Create your repo from the starter app. It already knows how to talk to the Spine.' },
  { n: '02', title: 'Build with AI', body: 'The build-gp-app skill guides you through building your screens, step by step.' },
  { n: '03', title: 'Submit for review', body: 'Tell us the name, repo and owner. GP Technology reviews it and wires it up.' },
  {
    n: '04',
    title: 'Go live',
    body: "Your app is switched on and appears in the App Store for the people it's meant for, labeled with its stage, from Demo to Live.",
  },
];

export const HOOD_SECTION = {
  kicker: 'Under the hood',
  title: 'For the builders in the room.',
  sub: 'The details your engineers will ask about, all in one place.',
  stackCta: 'See the full tech stack →',
  stackHref: '/about/stack',
} as const;

/* Exempt from the vocabulary guard: this is where the model and toolkit are named. */
export const HOOD_TILES: readonly HoodTile[] = [
  {
    title: 'Anthropic Claude',
    body: 'The model behind the AI features, called through the platform. Apps never hold a key.',
  },
  {
    title: 'gp-sdk',
    code: '@deloitte-us-consulting/gp-sdk',
    body: 'from GitHub Packages. Handles sign-in, access, AI and layout for your app.',
  },
  {
    title: 'React + Cloudscape',
    body: 'Apps are React screens that load into the shell, with pinned shared versions.',
  },
  {
    title: 'Serverless on AWS',
    body: 'Lambda and API Gateway on Deloitte AWS. No servers or containers to run.',
  },
];

export const CONTACT_SECTION = {
  kicker: 'Get in touch',
  title: 'Talk to Growth Platforms Technology.',
  sub: "Have an app idea, a question about the Spine, or need help getting live? We'd love to hear from you.",
  cta: 'Email the team →',
  askAboutPrefix: 'Talk to',
  askAboutSuffix: 'about',
} as const;
